import React, {useState} from 'react';
import {Alert, Button, CircularProgress, List, Stack, Typography} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import useSubscribeToChatsList from "./hooks/subscribeToChatsList";
import ChatItem from "./ChatItem";
import {useNavigate, useParams} from "react-router";
import {nanoid} from 'nanoid';
import usePolicyPageStore from '../../../store/usePolicyPageStore';
import {useSession} from "../../../../../firebase/UserProvider";
import useChatMessagesStore from '../../../store/useChatMessagesStore';

const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'}; }

const ListOfChats = () => {
    const navigate = useNavigate();
    const {chatId: currentChatId} = useParams();
    const {user} = useSession();
    const {setChatId} = usePolicyPageStore();
    const resetChat = useChatMessagesStore(s => s.resetChat);
    const [creating, setCreating] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const {chats: allChats, loading, error, removeChat} = useSubscribeToChatsList();
    // Only show FINTEL AI threads in this panel; expert human-to-human threads go to /chat/expert
    const chats = (allChats || []).filter(c => c.chatType === 'fintel');

    const handleDeleteChat = async (chat) => {
        if (deletingId) return;
        setDeletingId(chat.id);
        try {
            const res = await fetch(`${_awpBase}/api/chats/${encodeURIComponent(chat.id)}`, {
                method: 'DELETE', headers: _authH(),
            });
            if (!res.ok && res.status !== 404) {
                console.error('Failed to delete chat', res.status);
                return;
            }
            // Remove from local state immediately
            removeChat(chat.id);
            resetChat(chat.id);

            // If we just deleted the active thread, navigate to the next available one
            if (String(chat.id) === String(currentChatId)) {
                const remaining = chats.filter(c => c.id !== chat.id);
                if (remaining.length > 0) {
                    navigate(remaining[0].id, {replace: true});
                } else {
                    navigate('.', {replace: true, relative: 'path'});
                }
            }
        } catch (err) {
            console.error('Delete chat error', err);
        } finally {
            setDeletingId(null);
        }
    };

    const createNewThread = async () => {
        setCreating(true);
        try {
            const chatId = nanoid();
            await fetch(`${_awpBase}/api/chats`, {
                method: 'POST', headers: _authH(),
                body: JSON.stringify({id: chatId, title: 'New thread', chatType: 'fintel', owner: user.uid, participants: [user.uid], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()}),
            });

            // update page store and navigate to new chat
            setChatId(chatId);
            navigate(chatId);
        } catch (err) {
            console.error('Failed to create new chat', err);
        } finally {
            setCreating(false);
        }
    };


    return (
        <Stack sx={{width: '100%'}} flex={1}>
            <Stack p={2}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon/>}
                    onClick={createNewThread}
                    disabled={creating || loading}
                >
                    New Thread
                </Button>
            </Stack>

            {loading ? (
                <Stack alignItems="center" sx={{py: 3}}>
                    <CircularProgress size={28}/>
                </Stack>
            ) : error ? (
                <Alert severity="error">{String(error?.message || error)}</Alert>
            ) : chats?.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                    No chats yet.
                </Typography>
            ) : (
                <List disablePadding>
                    {chats.map((chat, idx) => (
                        <ChatItem
                            key={chat.id}
                            idx={idx}
                            chat={chat}
                            onDelete={handleDeleteChat}
                        />
                    ))}
                </List>
            )}
        </Stack>
    );
};

export default ListOfChats;
